/**
 * كتالوج التطوير التنظيمي.
 *
 * شكلُ الشركة لا أشخاصُها: أيُّ وظائفَ عندنا، وما وصفُ كلٍّ منها، وأيّ
 * درجةٍ هي، ومن يتبع من، وإلى أين يترقّى من يشغلها.
 *
 * ## والوظيفةُ غيرُ الموظّف
 *
 * فرقٌ يخلطه كثيرٌ من الأنظمة فيدفع ثمنَه لاحقًا: «مصمّم أوّل» وظيفةٌ لها
 * وصفٌ ودرجةٌ وعددُ شواغرَ معتمَد، و«أحمد» موظّفٌ يشغلها. وخلطُهما يجعل
 * إلغاءَ الوظيفة حذفًا للموظّف، ويجعل تخطيطَ القوى العاملة مستحيلًا —
 * لأنّه بالتعريف مقارنةُ المعتمَد بالمشغول.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   الجاهزية للإحلال
   ========================================================================== */

export const SUCCESSION_READINESS = ['NOW', 'SOON', 'LATER'] as const;
export type SuccessionReadiness = (typeof SUCCESSION_READINESS)[number];

export interface ReadinessDef extends Labelled<SuccessionReadiness> {
  description: string;
  /** ترتيبُ العرض — الجاهزُ أوّلًا. */
  order: number;
}

export const READINESS_DEFS: readonly ReadinessDef[] = [
  {
    key: 'NOW',
    label: 'جاهز الآن',
    kind: 'success',
    description: 'يشغلها غدًا لو خلت اليوم.',
    order: 1,
  },
  {
    key: 'SOON',
    label: 'خلال سنة أو سنتين',
    kind: 'warning',
    description: 'يحتاج خبرةً أو تدريبًا محدَّدًا.',
    order: 2,
  },
  {
    key: 'LATER',
    label: 'ثلاث سنوات أو أكثر',
    kind: 'neutral',
    description: 'إمكاناتٌ بعيدةُ النضج.',
    order: 3,
  },
] as const;

export const READINESS_MAP = Object.fromEntries(
  READINESS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<SuccessionReadiness, ReadinessDef>>;

/* ==========================================================================
   تخطيط القوى العاملة
   ========================================================================== */

export interface StaffingRow {
  positionId: string;
  title: string;
  department: string | null;
  gradeName: string | null;
  /** العددُ المعتمَد في الوصف الوظيفي. */
  approved: number;
  /** من يشغلها فعلًا الآن. */
  filled: number;
  /** ما يُبحث عنه في التوظيف الآن. */
  recruiting: number;
  gap: number;
  state: StaffingState;
}

export const STAFFING_STATES = ['SHORT', 'HIRING', 'FULL', 'OVER'] as const;
export type StaffingState = (typeof STAFFING_STATES)[number];

export interface StaffingStateDef extends Labelled<StaffingState> {
  description: string;
}

export const STAFFING_STATE_DEFS: readonly StaffingStateDef[] = [
  {
    key: 'SHORT',
    label: 'ناقص',
    kind: 'danger',
    description: 'شاغرٌ معتمَدٌ لا أحدَ فيه ولا بحثَ عنه.',
  },
  {
    key: 'HIRING',
    label: 'قيد التوظيف',
    kind: 'warning',
    description: 'ناقصٌ ولكنّ الوظيفةَ منشورةٌ ويُبحث عن شاغلها.',
  },
  { key: 'FULL', label: 'مكتمل', kind: 'success', description: 'المعتمَد مشغول.' },
  {
    key: 'OVER',
    label: 'زائد',
    kind: 'info',
    description: 'من يشغلها أكثرُ من المعتمَد — يُراجَع الاعتماد أو التوزيع.',
  },
] as const;

export const STAFFING_STATE_MAP = Object.fromEntries(
  STAFFING_STATE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<StaffingState, StaffingStateDef>>;

/**
 * حالةُ وظيفةٍ من أعدادها.
 *
 * «ناقصٌ ويُبحث عنه» غيرُ «ناقصٍ ولا أحدَ يبحث»: الأوّلُ عملٌ جارٍ،
 * والثاني هو ما يُعرض على الإدارة. وخلطُهما في «ناقص» يُغرق القائمةَ
 * بما هو قيد المعالجة فلا يُرى ما أُهمل.
 */
export function staffingState(
  approved: number,
  filled: number,
  recruiting: number,
): StaffingState {
  if (filled > approved) return 'OVER';
  if (filled === approved) return 'FULL';
  return recruiting > 0 ? 'HIRING' : 'SHORT';
}

/* ==========================================================================
   موضع الراتب في نطاق الدرجة
   ========================================================================== */

/**
 * أين يقع راتبٌ من نطاق درجته — نسبةٌ مئوية.
 *
 * مئةٌ تعني الوسطَ تمامًا، وأقلُّ من ثمانين تعني أنّ صاحبَه دون نطاقه.
 * وهذا هو الرقمُ الذي يُقارَن به السوقُ في كلّ مكان، فلا يُخترع غيرُه.
 *
 * ويُردّ `null` بلا وسطٍ مضبوط: نسبةٌ إلى صفرٍ لا معنى لها، وعرضُ صفرٍ
 * يجعل كلَّ راتبٍ يبدو خارج نطاقه.
 */
export function compaRatio(
  salary: number,
  midSalary: number | null | undefined,
): number | null {
  if (!midSalary || midSalary <= 0) return null;
  return Math.round((salary / midSalary) * 100);
}

export const COMPA_BANDS = [
  { upTo: 79, label: 'دون النطاق', kind: 'danger' as const },
  { upTo: 95, label: 'أسفل الوسط', kind: 'warning' as const },
  { upTo: 110, label: 'حول الوسط', kind: 'success' as const },
  { upTo: 125, label: 'أعلى الوسط', kind: 'info' as const },
  { upTo: null, label: 'فوق النطاق', kind: 'brand' as const },
] as const;

export function compaBand(ratio: number | null): (typeof COMPA_BANDS)[number] | null {
  if (ratio === null) return null;
  return (
    COMPA_BANDS.find((b) => b.upTo === null || ratio <= b.upTo) ??
    COMPA_BANDS[COMPA_BANDS.length - 1]!
  );
}

/* ==========================================================================
   الحدود
   ========================================================================== */

/** أقصى ما يُقبل من مهامٍّ أو مؤهّلاتٍ في وصفٍ وظيفيّ. */
export const MAX_DUTIES = 25;

/** أعمقُ ما يُرسم من الهيكل — حمايةٌ من حلقةٍ لا تنتهي. */
export const MAX_ORG_DEPTH = 12;
